<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ loading || loadingUser ? '...' : isPostAuthorBanned(post) ? t('PostPage.blockedContent') : post.title }}</ion-title>
        <ion-buttons slot="start">
          <ion-back-button :default-href="parentPath ? parentPath : '/posts'"></ion-back-button>
        </ion-buttons>
        <ion-buttons slot="primary" v-if="!canUpdatePost(post)">
          <ion-button @click="openPopover(post, $event)">
            <ion-icon slot="icon-only" :ios="ellipsisHorizontal" :md="ellipsisVertical"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content :fullscreen="true">
      <ion-refresher slot="fixed" @ionRefresh="fetchLatestPost(currPostId, $event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>

      <!-- Loading Data -->
      <div class="spin" v-if="loading || loadingUser">
        <ion-spinner></ion-spinner>
      </div>

      <!-- Data loaded -->
      <ion-grid fixed v-else>
        <ion-row v-if="post">
          <!-- Post Detail -->
          <ion-col size="12">
            <ion-card>
              <ion-row>
                <ion-col class="ion-text-center" size-xs="12" size-md="4" size-xl="3" v-if="!isPostAuthorBanned(post)">
                  <img class="post-image" :src="post.image" />
                </ion-col>
                <ion-col>
                  <ion-card-header>
                    <!--<ion-card-title>{{ post.title }}</ion-card-title>-->
                    <ion-card-subtitle>{{ formatDate(post.createdAt) }}</ion-card-subtitle>
                  </ion-card-header>
                  <ion-card-content>
                    <p v-if="isPostAuthorBanned(post)">{{ t('PostPage.blockedContent') }}</p>
                    <p style="white-space: pre-line" v-else>{{ post.content }}</p>
                    <ion-item class="ion-no-padding" lines="none">
                      <ion-badge slot="start">
                        <ion-icon style="cursor: pointer" :icon="thumbsUpOutline" @click="likePost(post)"></ion-icon> {{ post.likeCount }}
                        <ion-icon style="cursor: pointer" :icon="thumbsDownOutline" @click="dislikePost(post)"></ion-icon> {{ post.dislikeCount }}
                      </ion-badge>
                      {{ post.rating }}
                    </ion-item>
                    <small>{{ t('PostPage.lastUpdated') }}{{ formatDate(post.updatedAt) }}</small>
                    <ion-item class="ion-no-padding" lines="none">
                      <ion-chip>
                        <ion-avatar>
                          <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48cGF0aCBmaWxsPSIjYzVkYmZmIiBkPSJNMCAwaDUxMnY1MTJIMHoiLz48cGF0aCBkPSJNMjU2IDMwNGM2MS42IDAgMTEyLTUwLjQgMTEyLTExMlMzMTcuNiA4MCAyNTYgODBzLTExMiA1MC40LTExMiAxMTIgNTAuNCAxMTIgMTEyIDExMnptMCA0MGMtNzQuMiAwLTIyNCAzNy44LTIyNCAxMTJ2NTZoNDQ4di01NmMwLTc0LjItMTQ5LjgtMTEyLTIyNC0xMTJ6IiBmaWxsPSIjODJhZWZmIi8+PC9zdmc+" />
                        </ion-avatar>
                        <ion-label>{{ post.username || 'Unknown'}}</ion-label>
                      </ion-chip>
                      <!-- Actions for Post owner (update / delete) -->
                      <ion-buttons slot="end" v-if="canUpdatePost(post)">
                        <ion-button @click="openEditPostModal(post)">
                          <ion-icon slot="icon-only" :icon="create"></ion-icon>
                        </ion-button>
                        <ion-button @click="deletePost(post)">
                          <ion-icon slot="icon-only" :icon="trash"></ion-icon>
                        </ion-button>
                      </ion-buttons>
                    </ion-item>
                  </ion-card-content>
                </ion-col>
              </ion-row>
            </ion-card>
          </ion-col>

          <!-- Post Comments -->
          <ion-col size="12">
            <ion-card>
              <ion-card-header>
                <ion-card-title>{{ postComments.length }}{{ t('PostPage.numOfComments') }}</ion-card-title>
              </ion-card-header>
              <ion-item v-if="userLoggedIn">
                <ion-textarea v-model="newCommentContent" :placeholder="`${t('PostPage.newComment')}...`"></ion-textarea>
              </ion-item>
              <ion-item lines="none" v-if="userLoggedIn">
                <ion-buttons slot="end">
                  <ion-button size="small" color="primary" @click="addNewPostComment(post)">{{ t('PostPage.commentBtnText') }}</ion-button>
                </ion-buttons>
              </ion-item>
              <!-- User Comments -->
              <ion-row>
                <ion-col size="12" class="ion-no-padding" v-for="(comment, index) in postComments" :key="comment.id">
                  <ion-card>
                    <ion-card-header>
                      <ion-card-subtitle>
                        #{{ index+1 }} 
                        <ion-chip>
                          <ion-avatar>
                            <img src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48cGF0aCBmaWxsPSIjYzVkYmZmIiBkPSJNMCAwaDUxMnY1MTJIMHoiLz48cGF0aCBkPSJNMjU2IDMwNGM2MS42IDAgMTEyLTUwLjQgMTEyLTExMlMzMTcuNiA4MCAyNTYgODBzLTExMiA1MC40LTExMiAxMTIgNTAuNCAxMTIgMTEyIDExMnptMCA0MGMtNzQuMiAwLTIyNCAzNy44LTIyNCAxMTJ2NTZoNDQ4di01NmMwLTc0LjItMTQ5LjgtMTEyLTIyNC0xMTJ6IiBmaWxsPSIjODJhZWZmIi8+PC9zdmc+" />
                          </ion-avatar>
                          <ion-label>{{ comment.username }}</ion-label>
                        </ion-chip>
                      </ion-card-subtitle>
                    </ion-card-header>
                    <ion-card-content>
                      <p v-if="isPostAuthorBanned(comment)">{{ t('PostPage.blockedContent') }}</p>
                      <p style="white-space: pre-line" v-else>
                        {{ comment.content }}
                      </p>
                      <ion-item class="ion-no-padding" lines="full">
                        <ion-badge color="light" slot="start">
                          <ion-icon style="cursor: pointer" :icon="thumbsUpOutline" @click="likePostComment(comment)"></ion-icon> {{ comment.likeCount }}
                          <ion-icon style="cursor: pointer" :icon="thumbsDownOutline" @click="dislikePostComment(comment)"></ion-icon> {{ comment.dislikeCount }}
                        </ion-badge>
                      </ion-item>
                      <ion-item class="ion-no-padding" lines="none">
                        <ion-label><p><small>{{ formatDate(comment.createdAt) }}</small></p></ion-label>
                        <ion-buttons slot="end" v-if="!canUpdatePost(comment)">
                          <ion-button @click="openPopover(comment, $event, true)">
                            <ion-icon slot="icon-only" :ios="ellipsisHorizontal" :md="ellipsisVertical"></ion-icon>
                          </ion-button>
                        </ion-buttons>
                      </ion-item>
                    </ion-card-content>
                  </ion-card>
                </ion-col>
                <!-- Loading Comments -->
                <ion-col size="12">
                  <div class="ion-text-center" v-if="loadingPostComments">
                    <ion-spinner></ion-spinner>
                  </div>
                </ion-col>
              </ion-row>
            </ion-card>
          </ion-col>

        </ion-row>
      </ion-grid>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
// Vue reactivity
import { computed, ref } from 'vue';

// icons
import { thumbsUpOutline, thumbsDownOutline, create, trash, ellipsisHorizontal, ellipsisVertical, ban } from 'ionicons/icons';

// components
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
        IonGrid, IonRow, IonCol, IonSpinner, IonItem, IonLabel, IonChip, IonIcon, IonBackButton,
        IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle,
        IonBadge, IonTextarea, IonButton, IonButtons, IonAvatar,
        IonRefresher, IonRefresherContent, IonNote,
        modalController, alertController, loadingController, popoverController} from '@ionic/vue';
import PostModal from '@/components/PostModal.vue';
import PostActionMenu from '@/components/PostActionMenu.vue';

// API services
import PostService from '@/services/PostService';
import { utils } from '@/composables/utils';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

export default {
  name: 'PostDetailPage',
  components: { IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
                IonGrid, IonRow, IonCol, IonSpinner, IonItem, IonLabel, IonChip, IonIcon, IonBackButton,
                IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle,
                IonBadge, IonTextarea, IonButton, IonButtons, IonAvatar,
                IonRefresher, IonRefresherContent, IonNote, },
  setup() {
    const store = useStore();
    const { t } = useI18n();
    const route = useRoute();
    const currPostId = route.params.id; // post ID

    // 1. declare state variables (ref to make them reactive)
    const currUser = computed(() => store.state.user);
    const userLoggedIn = computed(() => store.state.loggedIn);
    const loading = computed(() => store.state.loadingPosts);
    const loadingUser = computed(() => store.state.loadingUser);
    const post = computed(() => store.getters.getPostById(currPostId));
    const postComments = ref<any>([]);
    const loadingPostComments = ref(false);
    const newCommentContent = ref("");

    // methods or filters
    const parentPath = route.params.parentPath; // may be from user posts page
    const router = useRouter();
    const { formatDate, presentToast, isPostAuthorBanned } = utils();
    
    const canUpdatePost = (post: any) => {
      return post.userId == currUser.value.id;
    }
    const deletePost = async (post: any) => {
      const alert = await alertController.create({
        header: t('PostPage.confirmDelete'),
        message: t('PostPage.confirmDeletePost'),
        buttons: [
          {
            text: t('cancel'),
            role: 'cancel',
            cssClass: 'secondary'
          },
          {
            text: t('PostPage.deletePost'),
            handler: async () => {
              const loading = await loadingController.create({});
              await loading.present();
              await PostService.deletePost(post.id, post.image);
              store.commit('deletePost', post.id);
              loading.dismiss();
              presentToast( t('PostPage.postDeleted'), 2000 );
              router.push('/posts');
            },
          },
        ],
      });
      return alert.present();
    }

    const likePost = (post: any) => {
      if (userLoggedIn.value == true) {
        post.likeCount++;
        PostService.updatePostReaction(post.id, "like");
      }
    }
    const dislikePost = (post: any) => {
      if (userLoggedIn.value == true) {
        post.dislikeCount++;
        PostService.updatePostReaction(post.id, "dislike");
      }
    }
    const likePostComment = (comment: any) => {
      if (userLoggedIn.value == true) {
        comment.likeCount++;
        PostService.updatePostCommentReaction(comment.id, "like");
      }
    }
    const dislikePostComment = (comment: any) => {
      if (userLoggedIn.value == true) {
        comment.dislikeCount++;
        PostService.updatePostCommentReaction(comment.id, "dislike");
      }
    }

    const fetchPostComments = (postId: any, e: any = null) => {
      loadingPostComments.value = true;
      PostService.getPostComments(postId).then(res => {
        loadingPostComments.value = false;
        postComments.value = res;
        if (e) e.target.complete(); // dismiss ion-refresher
      });
    }
    const fetchLatestPost = (postId: any, e: any = null) => {
      PostService.getPostById(postId).then(res => {
        store.commit('updatePost', res);
      });
      fetchPostComments(postId);
    }

    const addNewPostComment = async (post: any) => {
      if (newCommentContent.value) {
        // call API to add post comment
        const loading = await loadingController.create({});
        await loading.present();
        PostService.addNewPostComment(post.id, newCommentContent.value).then(res => {
          loading.dismiss();
          newCommentContent.value = ""; // reset textarea value
          postComments.value.push(res.data[0]); // add the new comment to the list
        });
      }
    }
    
    const openEditPostModal = async () => {
      const modal = await modalController.create({
        component: PostModal,
        componentProps: { editingPost: post },
      });
      modal.onDidDismiss().then((data) => {
        fetchLatestPost(currPostId);
      })
      return modal.present();
    }

    const openPopover = async (post: any, ev: any, isPostComment = false) => {
      const options: any = {
        component: PostActionMenu,
        event: ev,
        componentProps: { post, isPostComment },
      };
      const popover = await popoverController.create(options);
      await popover.present();
    };

    fetchPostComments(currPostId);

    // 2. fetch data from API and store data in state variables (optionally with polling)
    //const polling = setInterval(fetchLatestPost, 5000, currPostId);
    //onUnmounted(() => {
    //  clearInterval(polling);
    //});

    // 3. return variables & methods to be used in template HTML
    return {
      t,

      // icons
      thumbsUpOutline, thumbsDownOutline, create, trash, ellipsisHorizontal, ellipsisVertical, ban,

      // variables
      userLoggedIn, currPostId, parentPath, loading, loadingPostComments, loadingUser,
      post, postComments, newCommentContent,

      // methods
      fetchLatestPost, formatDate, likePost, dislikePost,
      openEditPostModal, deletePost, canUpdatePost,
      likePostComment, dislikePostComment, addNewPostComment,
      openPopover, isPostAuthorBanned,
    }
  },
}
</script>

<style scoped>
  .post-image {
    display: block;
    margin: auto;
    max-height: 300px;
  }
</style>